import { IChoiceGroupOption } from '@fluentui/react/lib/ChoiceGroup';
import { IPCheckbox, IPChoiceGroup, IPLabel, IPPrimaryButton, IPText, IPTextField } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalStackTokensGapSmall, globalTextStylesBold } from 'globalStyles';
import { CopilotQuestionOption } from 'models/copilot/copilotQuestionOption';
import {
  AutomationQuestion,
  AutomationQuestionAnswer,
  AutomationQuestionSetPayload,
  questionAnswerTextMaxLength,
} from 'models/copilot/questionSet';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IQuestionDateDraft, isDateAnswered, QuestionDate, toDateSelection } from './QuestionDate';
import { QuestionLookup } from './QuestionLookup';

// the choice key of the own answer of a single question; option ids are the platform's (o1, o2, ...)
const otherValue = '__other';

// a date or dateRange question keeps its chip and its Other dates in the IQuestionDateDraft part
interface IDraftAnswer extends IQuestionDateDraft {
  optionIds: string[];
  other: string;
  text: string;
  // a lookup question: the chosen rows of its source
  rows: CopilotQuestionOption[];
}

// the drafts a card starts with: a single question with an option the server marks selected starts with that option
// chosen; any other question starts empty
const getPreselectedDrafts = (questions: AutomationQuestion[]): Record<string, IDraftAnswer> => {
  const drafts: Record<string, IDraftAnswer> = {};
  questions.forEach((question, index) => {
    if (question.kind !== 'single') return;

    const options = question.options ?? [];
    const optionIndex = options.findIndex((option) => option.selected === true);
    if (optionIndex < 0) return;

    // the same keys the card uses: the question's id or __q<index>, the option's id or __o<index>
    drafts[question.id ?? `__q${index}`] = {
      optionIds: [options[optionIndex].id ?? `__o${optionIndex}`],
      other: '',
      text: '',
      rows: [],
    };
  });

  return drafts;
};

interface IQuestionCardProps {
  questionSet: AutomationQuestionSetPayload;
  // the sent answers: the card is a read-only record of them
  answers?: AutomationQuestionAnswer[];
  // the card of the most recent item while nothing runs, or the card the session waits on; otherwise a record
  // without inputs
  canAnswer: boolean;
  // the user answered the card by typing a message instead: a read-only record of the questions
  answeredInChat?: boolean;
  onSubmit: (answers: AutomationQuestionAnswer[]) => void | Promise<void>;
}

/**
 * The question card of SkillControl's drafter (phase 32): typed questions answered in place -- single-select as a
 * choice group, multi-select as checkboxes, free text as a text field, an "other" escape where the drafter allows it.
 * Only the most recent card takes answers, a guard stops double submits, and a decided card shows a read-only record.
 * A lookup question (the general knowledge loop's) picks the tenant's own rows in a combobox (QuestionLookup); a
 * date or dateRange question picks a preset chip or Other's date field (QuestionDate).
 * Ported from skillcontrol-web components/Copilot/Questions/QuestionCard.tsx (Fluent v9) to Fluent v8 (D32-2); keep
 * the two in step.
 */
export const QuestionCard = (props: IQuestionCardProps) => {
  const { t } = useTranslation(['copilot']);
  const [drafts, setDrafts] = useState<Record<string, IDraftAnswer>>(() =>
    getPreselectedDrafts(props.questionSet.questions ?? []),
  );
  // set from the submit until it is handled, so a double click sends one set of answers
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const questions = props.questionSet.questions ?? [];

  // the fallback key cannot collide with a platform id (q1, o1, ...) when ids are only partly present (review fix WB3)
  const keyOf = (question: AutomationQuestion, index: number): string => question.id ?? `__q${index}`;

  const draftOf = (key: string): IDraftAnswer => drafts[key] ?? { optionIds: [], other: '', text: '', rows: [] };

  const setDraft = (key: string, change: Partial<IDraftAnswer>) => {
    setDrafts((current) => ({ ...current, [key]: { ...draftOf(key), ...change } }));
  };

  // an unrecognized kind renders and validates as free text, so a required question never blocks the submit
  const kindOf = (question: AutomationQuestion): string => {
    if (question.kind === 'lookup' && question.source) return 'lookup';

    return question.kind === 'single' ||
      question.kind === 'multi' ||
      question.kind === 'date' ||
      question.kind === 'dateRange'
      ? question.kind
      : 'text';
  };

  const isAnswered = (question: AutomationQuestion, key: string): boolean => {
    const draft = draftOf(key);
    switch (kindOf(question)) {
      case 'single':
        return draft.optionIds[0] === otherValue ? draft.other.trim() !== '' : draft.optionIds.length === 1;
      case 'multi':
        return draft.optionIds.length > 0 || (question.allowOther === true && draft.other.trim() !== '');
      case 'lookup':
        return draft.rows.length > 0 || (question.allowOther === true && draft.other.trim() !== '');
      case 'date':
      case 'dateRange':
        return isDateAnswered(question, kindOf(question) === 'dateRange', draft);
      default:
        return draft.text.trim() !== '';
    }
  };

  const canSubmit = questions.every(
    (question, index) => question.required !== true || isAnswered(question, keyOf(question, index)),
  );

  const toAnswer = (question: AutomationQuestion, key: string): AutomationQuestionAnswer => {
    const draft = draftOf(key);
    const kind = kindOf(question);
    if (kind === 'text') {
      return {
        questionId: question.id,
        question: question.text,
        selectedLabels: [],
        text: draft.text.trim() || undefined,
      };
    }

    // the chosen rows with their ids verbatim, so the answer line names "label (id)" for the model
    if (kind === 'lookup') {
      return {
        questionId: question.id,
        question: question.text,
        selectedLabels: draft.rows.map((row) => row.label),
        selectedIds: draft.rows.map((row) => row.id),
        text: question.allowOther === true ? draft.other.trim() || undefined : undefined,
      };
    }

    // the label for the user and the ISO date (a range: from..to) for the model: "In 2 weeks (2026-11-09)"
    if (kind === 'date' || kind === 'dateRange') {
      const selection = toDateSelection(t, question, kind === 'dateRange', draft);

      return {
        questionId: question.id,
        question: question.text,
        selectedLabels: selection ? [selection.label] : [],
        selectedIds: selection ? [selection.id] : undefined,
      };
    }

    const chosen = (question.options ?? []).filter(
      (option, index) => draft.optionIds.indexOf(option.id ?? `__o${index}`) >= 0,
    );
    // a candidate card's options name records: their ids travel like a lookup's, so the line reads "label (id)"
    const hasRecords = chosen.some((option) => !!option.recordId);
    // a single question sends the own text only while Other is the choice
    const isOtherChosen = kind === 'multi' || draft.optionIds[0] === otherValue;

    return {
      questionId: question.id,
      question: question.text,
      selectedLabels: chosen.map((option) => option.label),
      selectedIds: hasRecords ? chosen.map((option) => option.recordId ?? '') : undefined,
      text: isOtherChosen ? draft.other.trim() || undefined : undefined,
    };
  };

  const submit = async () => {
    if (isSubmitting || !canSubmit) return;

    const answers = questions.map((question, index) => toAnswer(question, keyOf(question, index)));

    setIsSubmitting(true);
    try {
      await props.onSubmit(answers);
    } finally {
      setIsSubmitting(false);
    }
  };

  // the sent answers as a read-only record, one line per answered question
  if (props.answers) {
    return (
      <IPStack tokens={{ childrenGap: 4 }}>
        <IPText variant="small">{t('copilot:Chat.Questions.Answered')}</IPText>
        {props.answers
          .filter((answer) => answer.selectedLabels.length > 0 || answer.text)
          .map((answer, index) => (
            <IPText key={answer.questionId ?? index} variant="small">
              {answer.question}
              {' -> '}
              {[answer.selectedLabels.join(', '), answer.text].filter(Boolean).join('; ')}
            </IPText>
          ))}
      </IPStack>
    );
  }

  // without the inputs the card is a record of what was asked
  if (props.answeredInChat || !props.canAnswer) {
    return (
      <IPStack tokens={{ childrenGap: 4 }}>
        {props.answeredInChat && <IPText variant="small">{t('copilot:Chat.Questions.AnsweredInChat')}</IPText>}
        {questions.map((question, index) => (
          <IPText key={keyOf(question, index)} variant="small">
            {question.text}
          </IPText>
        ))}
      </IPStack>
    );
  }

  const renderOther = (question: AutomationQuestion, key: string, isEnabled: boolean) => {
    if (question.allowOther !== true) return null;

    return (
      <IPTextField
        value={draftOf(key).other}
        disabled={!isEnabled}
        maxLength={questionAnswerTextMaxLength}
        placeholder={t('copilot:Chat.Questions.OtherPlaceholder')}
        onChange={(_ev, value) => setDraft(key, { other: value ?? '' })}
      />
    );
  };

  const renderInputs = (question: AutomationQuestion, key: string) => {
    const draft = draftOf(key);
    const kind = kindOf(question);

    if (kind === 'lookup') {
      return (
        <>
          <QuestionLookup question={question} selected={draft.rows} onChange={(rows) => setDraft(key, { rows })} />
          {renderOther(question, key, true)}
        </>
      );
    }

    if (kind === 'date' || kind === 'dateRange') {
      return (
        <QuestionDate
          question={question}
          isRange={kind === 'dateRange'}
          draft={draft}
          onChange={(change) => setDraft(key, change)}
        />
      );
    }

    if (kind === 'single') {
      const options: IChoiceGroupOption[] = (question.options ?? []).map((option, optionIndex) => ({
        key: option.id ?? `__o${optionIndex}`,
        text: option.description ? `${option.label} - ${option.description}` : option.label,
      }));
      if (question.allowOther === true) {
        options.push({ key: otherValue, text: t('copilot:Chat.Questions.Other') });
      }

      return (
        <>
          <IPChoiceGroup
            label={question.text}
            required={question.required === true}
            options={options}
            selectedKey={draft.optionIds[0]}
            onChange={(_ev, option) => option && setDraft(key, { optionIds: [option.key] })}
          />
          {renderOther(question, key, draft.optionIds[0] === otherValue)}
        </>
      );
    }

    if (kind === 'multi') {
      return (
        <>
          <IPLabel required={question.required === true}>{question.text}</IPLabel>
          {(question.options ?? []).map((option, optionIndex) => {
            const optionKey = option.id ?? `__o${optionIndex}`;

            return (
              <IPCheckbox
                key={optionKey}
                checked={draft.optionIds.indexOf(optionKey) >= 0}
                label={option.description ? `${option.label} - ${option.description}` : option.label}
                onChange={(_ev, checked) =>
                  setDraft(key, {
                    optionIds:
                      checked === true
                        ? [...draft.optionIds, optionKey]
                        : draft.optionIds.filter((id) => id !== optionKey),
                  })
                }
              />
            );
          })}
          {renderOther(question, key, true)}
        </>
      );
    }

    return (
      <IPTextField
        label={question.text}
        required={question.required === true}
        multiline
        rows={3}
        maxLength={questionAnswerTextMaxLength}
        value={draft.text}
        onChange={(_ev, value) => setDraft(key, { text: value ?? '' })}
      />
    );
  };

  const renderQuestion = (question: AutomationQuestion, index: number) => {
    const key = keyOf(question, index);

    //the header chip above each question, as the v9 original renders it (review fix WB1; v8 has no Badge wrapper)
    return (
      <IPStack key={key} tokens={{ childrenGap: 4 }}>
        {question.header ? <IPText variant="small">{question.header}</IPText> : null}
        {renderInputs(question, key)}
      </IPStack>
    );
  };

  return (
    <IPStack tokens={globalStackTokensGapSmall}>
      <IPText styles={globalTextStylesBold}>{t('copilot:Chat.Questions.Title')}</IPText>
      {questions.map(renderQuestion)}
      <IPStack.Item>
        <IPPrimaryButton
          text={t('copilot:Chat.Questions.Submit')}
          disabled={!canSubmit || isSubmitting}
          onClick={() => void submit()}
        />
      </IPStack.Item>
    </IPStack>
  );
};

export default QuestionCard;
